import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Select,
  SelectItem,
  Button,
  Switch,
  Chip,
} from '@nextui-org/react';
import { FaCalendar, FaInfinity } from 'react-icons/fa';
import Icon from '../base/Icon';

export interface DateRange {
  startDate: string; // YYYY-MM-DD format
  endDate: string; // YYYY-MM-DD format
  isAllTime: boolean;
}

interface DateRangeSelectorProps {
  className?: string;
  onDateRangeChange: (dateRange: DateRange) => void;
  defaultToCurrentMonth?: boolean;
  apiKeys?: { key_id: string; name: string; is_active: boolean }[];
  selectedApiKeyId?: string;
  onApiKeyChange?: (keyId: string) => void;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  className = '',
  onDateRangeChange,
  defaultToCurrentMonth = true,
  apiKeys,
  selectedApiKeyId,
  onApiKeyChange,
}) => {
  const [isAllTime, setIsAllTime] = useState(true); // Default to All Time
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');

  // Generate month options
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  // Generate year options (current year - 2 to current year)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => {
    const year = currentYear - 2 + i;
    return { value: year.toString(), label: year.toString() };
  });

  // Initialize with current month if defaultToCurrentMonth is true and not in All Time mode
  useEffect(() => {
    if (defaultToCurrentMonth && !isAllTime) {
      const now = new Date();
      const currentMonth = (now.getMonth() + 1).toString().padStart(2, '0');
      const currentYearStr = now.getFullYear().toString();

      setStartMonth(currentMonth);
      setStartYear(currentYearStr);
      setEndMonth(currentMonth);
      setEndYear(currentYearStr);
    }
  }, [defaultToCurrentMonth, isAllTime]);

  // Handle date range change
  useEffect(() => {
    if (isAllTime) {
      onDateRangeChange({
        startDate: '',
        endDate: '',
        isAllTime: true,
      });
    } else if (startMonth && startYear && endMonth && endYear) {
      // Convert MM/YYYY to date range
      const startDate = `${startYear}-${startMonth}-01`;

      // End date should be the last day of the end month
      const endDate = new Date(parseInt(endYear), parseInt(endMonth), 0)
        .toISOString()
        .split('T')[0];

      onDateRangeChange({
        startDate,
        endDate,
        isAllTime: false,
      });
    }
  }, [isAllTime, startMonth, startYear, endMonth, endYear, onDateRangeChange]);

  const formatDateRange = () => {
    if (isAllTime) {
      return 'All Time';
    }
    if (startMonth && startYear && endMonth && endYear) {
      const startMonthName = months.find((m) => m.value === startMonth)?.label;
      const endMonthName = months.find((m) => m.value === endMonth)?.label;

      if (startYear === endYear && startMonth === endMonth) {
        return `${startMonthName} ${startYear}`;
      }
      return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
    }
    return 'Select Date Range';
  };

  const resetToCurrentMonth = () => {
    const now = new Date();
    const currentMonth = (now.getMonth() + 1).toString().padStart(2, '0');
    const currentYearStr = now.getFullYear().toString();

    setStartMonth(currentMonth);
    setStartYear(currentYearStr);
    setEndMonth(currentMonth);
    setEndYear(currentYearStr);
    setIsAllTime(false);
  };

  return (
    <Card className={className}>
      <CardBody className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon icon={FaCalendar} className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold">Filters</h3>
          </div>
          <Chip
            color={isAllTime ? 'secondary' : 'primary'}
            variant="flat"
            size="sm"
            startContent={
              isAllTime ? (
                <Icon icon={FaInfinity} className="w-3 h-3" />
              ) : (
                <Icon icon={FaCalendar} className="w-3 h-3" />
              )
            }
          >
            {formatDateRange()}
          </Chip>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">All Time Data</span>
            <Switch
              size="sm"
              isSelected={isAllTime}
              onValueChange={setIsAllTime}
              color="secondary"
            />
          </div>

          {apiKeys && apiKeys.length > 0 && onApiKeyChange && (
            <div className="space-y-2">
              <div className="text-xs text-gray-500 font-medium">API KEY</div>
              <Select
                placeholder="All API Keys"
                size="sm"
                items={[
                  { key_id: '', name: 'All API Keys', is_active: true },
                  ...apiKeys,
                ]}
                selectedKeys={selectedApiKeyId ? [selectedApiKeyId] : ['']}
                onChange={(e) => onApiKeyChange(e.target.value)}
              >
                {(key) => (
                  <SelectItem key={key.key_id}>
                    {key.name + (!key.is_active ? ' (revoked)' : '')}
                  </SelectItem>
                )}
              </Select>
            </div>
          )}

          {!isAllTime && (
            <>
              <div className="space-y-2">
                <div className="text-xs text-gray-500 font-medium">FROM</div>
                <div className="flex gap-2">
                  <Select
                    placeholder="Month"
                    size="sm"
                    value={startMonth}
                    onChange={(e) => setStartMonth(e.target.value)}
                    className="flex-1"
                  >
                    {months.map((month) => (
                      <SelectItem key={month.value} value={month.value}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select
                    placeholder="Year"
                    size="sm"
                    value={startYear}
                    onChange={(e) => setStartYear(e.target.value)}
                    className="flex-1"
                  >
                    {years.map((year) => (
                      <SelectItem key={year.value} value={year.value}>
                        {year.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs text-gray-500 font-medium">TO</div>
                <div className="flex gap-2">
                  <Select
                    placeholder="Month"
                    size="sm"
                    value={endMonth}
                    onChange={(e) => setEndMonth(e.target.value)}
                    className="flex-1"
                  >
                    {months.map((month) => (
                      <SelectItem key={month.value} value={month.value}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select
                    placeholder="Year"
                    size="sm"
                    value={endYear}
                    onChange={(e) => setEndYear(e.target.value)}
                    className="flex-1"
                  >
                    {years.map((year) => (
                      <SelectItem key={year.value} value={year.value}>
                        {year.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  variant="flat"
                  onPress={resetToCurrentMonth}
                  className="flex-1"
                >
                  Current Month
                </Button>
              </div>
            </>
          )}
        </div>

        {!isAllTime && startMonth && startYear && endMonth && endYear && (
          <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Selected:</strong> {formatDateRange()}
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default DateRangeSelector;
